import { catchErrors } from 'puffy-core/error'
import { Hub } from 'aws-amplify'
import { Auth } from '../auth'
import { CONFIRM_CODE_PATH, FORGOT_PWD_PATH, HOME_PATH } from '../path'

export const SIGNUP_SUB_KEY = 'goanna-wand-signup-sub'
export const SIGNUP_EMAIL_KEY = 'goanna-wand-signup-email'
// Where to send the user once their email is verified: 'home' (auto sign-in after a fresh sign-up), 'resetpwd' or 'login'.
export const CONFIRM_NEXT_KEY = 'goanna-wand-confirm-next'
export const RESEND_COOLDOWN_SEC = 60

// Cognito error messages that identify an account that exists but whose email was never verified (i.e., UNCONFIRMED).
export const UNVERIFIED_EMAIL_ERROR = 'no registered/verified email'
export const USER_EXISTS_ERRORS = ['User already exists', 'account with the given email already exists']

// Mirrors the Cognito user pool password policy so users see what is missing before submitting.
// Symbols: https://docs.aws.amazon.com/cognito/latest/developerguide/user-pool-settings-policies.html
export const PASSWORD_RULES = [
	{ label:'At least 6 characters', test:(p:string) => p.length >= 6 },
	{ label:'A lowercase letter', test:(p:string) => /[a-z]/.test(p) },
	{ label:'An uppercase letter', test:(p:string) => /[A-Z]/.test(p) },
	{ label:'A number', test:(p:string) => /[0-9]/.test(p) },
	// Cognito also counts a space as a symbol, but not a leading or trailing one.
	{ label:'A symbol (e.g. ! @ # $ % or a space)', test:(p:string) => /[\^$*.[\]{}()?"!@#%&/\\,><':;|_~`=+-]/.test(p) || / /.test(p.trim()) }
]

export const get_password_issues = (password:string) => PASSWORD_RULES.filter(r => !r.test(password||'')).map(r => r.label.toLowerCase())

export const errors_to_message = (errors:any[]) => (errors||[]).map((e:any) => e.message).join(' ') || ''

export const has_error = (errors:any[], fragments:string[]) => {
	const msg = errors_to_message(errors)
	return fragments.some(f => msg.indexOf(f) >= 0)
}

export const store_confirm_context = (email:string, next:string) => {
	localStorage.setItem(SIGNUP_SUB_KEY, email)
	localStorage.setItem(SIGNUP_EMAIL_KEY, email)
	localStorage.setItem(CONFIRM_NEXT_KEY, next)
}

/**
 * Emails a new verification code to an account that exists but was never verified (UNCONFIRMED).
 *
 * WARNING: The app client has 'PreventUserExistenceErrors' enabled, so Cognito also reports success (without sending
 * anything) when the email is unknown or the account is already confirmed. A success therefore does not prove the
 * account is UNCONFIRMED. Use 'recover_existing_account' to find out.
 *
 * @param  {String}	email
 *
 * @return {Object}	{ sent, error }
 */
export const resend_code = async (email:string) => {
	// Doc: https://docs.amplify.aws/lib/auth/emailpassword/q/platform/js/#re-send-sign-up-confirmation-code
	const [errors] = await catchErrors(Auth.resendSignUp(email))
	return errors
		? { sent:false, error:errors_to_message(errors) }
		: { sent:true, error:'' }
}

/**
 * Called when sign-up fails because the email is already registered. Works out the state of that account and sends
 * the user to the right place:
 * 	1. Signs in with the password just typed. Correct and confirmed: the user is logged in. Correct but never
 * 	   verified: a new code is emailed and the user goes to the code page.
 * 	2. Otherwise (wrong password), opens the Forgot password page with the email pre-filled. Nothing is emailed until
 * 	   the user asks for the reset there; that page also recovers UNCONFIRMED accounts.
 *
 * @param  {String}		email
 * @param  {String}		password
 * @param  {Object}		history
 * @param  {Function}	set_error
 * @param  {Object}		toast
 * @param  {String}		redirect_url
 *
 * @return {Void}
 */
export const recover_existing_account = async (email:string, password:string, history:any, set_error:Function, toast:any, redirect_url?:string) => {
	// 1. The user may simply have signed up before with the same password.
	const [signin_errors] = await catchErrors(Auth.signIn(email, password))
	if (!signin_errors) {
		toast.show('You already had a WAND account with this email, so we have logged you in.')
		history.push(redirect_url || HOME_PATH)
		return
	}
	if (has_error(signin_errors, ['is not confirm'])) {
		const { sent, error } = await resend_code(email)
		if (sent) {
			store_confirm_context(email, 'login')
			history.push(`${CONFIRM_CODE_PATH}?reason=unverified`)
		} else
			set_error(error)
		return
	}

	// 2. Wrong password: never email a reset code nobody asked for (anyone who knows an address could trigger one).
	// The Forgot password page, pre-filled, lets the user request it, and also recovers UNCONFIRMED accounts.
	history.push(`${FORGOT_PWD_PATH}?email=${encodeURIComponent(email)}&reason=exists`)
}

/**
 * Waits for Amplify's auto sign-in, which runs after 'confirmSignUp' when the user signed up in this session.
 * Must be called BEFORE 'confirmSignUp' so the event is not missed.
 *
 * @param  {Number}		timeout_ms
 *
 * @return {Promise<Boolean>}	true if the user is now signed in.
 */
export const wait_for_auto_sign_in = (timeout_ms:number=10000) => new Promise<boolean>(resolve => {
	const done = (signed_in:boolean) => {
		Hub.remove('auth', listener)
		clearTimeout(timer)
		resolve(signed_in)
	}
	const listener = ({ payload }:any) => {
		if (payload.event == 'autoSignIn')
			done(true)
		else if (payload.event == 'autoSignIn_failure')
			done(false)
	}
	const timer = setTimeout(() => done(false), timeout_ms)
	Hub.listen('auth', listener)
})
